import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart3, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { r14UiEnabled } from '../lib/r14Switch';
import SlotHistoryPanels from '../components/SlotHistoryPanels';

/*
 * Slot statistics - deliberately NOT a dashboard, until there is something to put in one.
 *
 * WHAT WAS HERE. A full stats dashboard ported from the Python V2 product, ~760 lines: all-time
 * stat cards, an action-distribution pie, hourly/daily/weekly tabs, a scheduler panel and a session
 * history list. It kept V2's own inline fetch client and called four routes this server does not
 * map, and the dev proxy does not forward:
 *
 *     GET /api/slots/{id}                  GET /api/worker/runner-state/{id}
 *     GET /api/worker/status/{id}          GET /api/sessions/?slot_id=
 *
 * It was worse than broken, it was CONFIDENT. The slot-details query destructured only
 * { data, isLoading } - no isError - so its failure was swallowed silently and the header
 * fabricated a slot name from the URL segment. Every figure fell through `|| 0`, so a slot that had
 * been running for weeks rendered a fully laid-out dashboard reading zero everywhere, with grey X
 * badges positively asserting that no follows, likes, comments or unfollows were scheduled today.
 * Only the runner query showed a banner, and the zeroed dashboard rendered underneath it anyway.
 *
 * WHY IT WAS NOT SIMPLY WIRED UP. Checked against the live schema on 2026-08-28:
 *
 *   - There is NO session table and NO session entity anywhere in the Spring server. "Recent
 *     Sessions" and the daily chart derived from it have no data source at all - that is a feature
 *     to build, not an endpoint to expose.
 *   - Of the 24 `runner.*` fields the page bound, FOUR exist on IGAccRunner (sessionStartDateTime,
 *     upcomingDayStart/StopDateTime, todaysSessionCount). The rest - hour and week windows,
 *     per-action limits-remaining, do-we-X-today, pending-today - are V2 inventions with nothing
 *     behind them.
 *   - The stats that DO exist (IGAccStats `total_*_done` plus the period counters, which
 *     SlotServiceImpl:4991 writes from the worker's sessionStats PUT) read ZERO on 11,041 of 11,044
 *     slots. An endpoint serving them faithfully would render a zeroed page indistinguishable from
 *     the bug it replaced.
 *
 * So the honest state is this one: say there is nothing to show and say why. The moment per-slot
 * stats are actually being recorded, the work is a /uiapi/slots/{slotName}/stats route over
 * SlotService.getSlotStats (which already backs /api/e/getSlotStatsV2) plus whichever panels the
 * data can honestly fill.
 *
 * The previous implementation is in git at cfcadbd if any of its layout is wanted back. Do not
 * restore it against defaults.
 *
 * ROUND 14 (2026-09-28). The server now keeps a 90-day daily history for welcome DMs, story views,
 * story likes and posts next to the likes/comments/follows/unfollows ones, and serves them at
 * /uiapi/slots/{slotName}/stats. components/SlotHistoryPanels draws them - and still says so,
 * per metric, when a history is not recorded, instead of drawing zeros. Behind lib/r14Switch;
 * OFF renders exactly the card below.
 *
 * ROUND 14b (2026-09-29): SlotHistoryPanels redrawn as a stats site (range control, headline tiles,
 * titled-axis charts with tooltips and table twins, projection, daily table); see its header. The
 * header names the SLOT (it is what the page is about), "Statistics" is the caption, the one back
 * link is the "<- Slots" above the title, and the panels put the time range in the header's action
 * slot and the "profile checked / data age" line under the title (renderHeader).
 */
export default function SlotStatsPage() {
  const { slotId } = useParams();
  const navigate = useNavigate();
  usePageTitle('Slot stats');
  const r14 = r14UiEnabled();

  const header = ({ actions, meta } = {}) => (
    <PageHeader
      icon={BarChart3}
      title={slotId}
      description="Statistics"
      back={{ to: '/slots', label: 'Slots' }}
      actions={actions}
    >
      {meta}
    </PageHeader>
  );

  return (
    <div data-testid="slot-stats-page">
      {/* Round 14 (lib/r14Switch): the histories. OFF = the round-13 card below, unchanged. */}
      {r14 ? <SlotHistoryPanels slotName={slotId} renderHeader={header} /> : (
      <>
      {header()}
      <Card className="aurora-card" data-testid="slot-stats-unavailable">
        <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Info className="h-4 w-4 text-accent" aria-hidden />
            Per-slot statistics are not available yet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-5 pt-0 text-sm text-muted-foreground sm:p-6 sm:pt-0">
          <p className="text-pretty">
            This page used to show a full dashboard for {slotId}. Every figure on it was a
            placeholder: it called endpoints that do not exist on this server, and displayed zeros
            rather than saying so. A page that reads <span className="font-medium text-foreground">0
            follows</span> for a slot that has been running is worse than a page that admits it does
            not know, so it has been taken out.
          </p>
          <p className="text-pretty">
            Bringing it back needs two things, in this order: per-slot activity actually being
            recorded (the counters exist and are wired, but read zero on essentially every slot
            today), and a decision on session history, for which there is currently no table.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" className="tap rounded-full"
              onClick={() => navigate(`/slots/${slotId}/settings`)}>
              Slot settings
            </Button>
            <Button variant="ghost" size="sm" className="tap rounded-full"
              onClick={() => navigate('/slots')}>
              Back to slots
            </Button>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
